import { Injectable, Injector, Type } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { from, isObservable, Observable, of } from 'rxjs';
import { concatMap, map, switchMap, take } from 'rxjs/operators';
import { DesignService, isNewDesign } from './design.service';
import { DESIGN_TWINS } from './design-twins';
import { brandInPath } from '../brand/brand-context';

type GuardResult = boolean | UrlTree;

/** Route data read by DesignRouteGuard. */
export interface DesignRouteData {
  /**
   * Guards to run (in order) once the page is *not* being redirected to its
   * redesigned twin — the route's own guards (AuthGuard, GuestGuard …). They
   * live here rather than beside DesignRouteGuard in `canActivate` so that a
   * guard with side effects (AuthGuard navigates guests to guest checkout)
   * never runs for a page that is about to be replaced.
   */
  designThen?: Type<CanActivate>[];
}

/**
 * Sends a legacy page to its redesigned twin when `TemplateData.NewDesign` is
 * on (see DesignService, design-twins.ts). Keeps the brand, the rest of the
 * path (e.g. the dine-in table id), the query string and the fragment.
 *
 * Put it on the legacy route only, as the route's sole `canActivate`; the
 * route's own guards go in `data.designThen`.
 */
@Injectable({ providedIn: 'root' })
export class DesignRouteGuard implements CanActivate {
  constructor(
    private design: DesignService,
    private router: Router,
    private injector: Injector
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<GuardResult> {
    return this.design.webCustomer$.pipe(
      take(1),
      switchMap((res) => {
        const redirect = isNewDesign(res)
          ? this.twinUrl(state.url, !!res?.TemplateData?.CheckoutDetail?.DistributorLogin)
          : null;
        if (redirect) return of(redirect);
        return this.runThen(route, state);
      })
    );
  }

  /** The twin's UrlTree, or null when this URL stays where it is. */
  private twinUrl(url: string, distributorLogin: boolean): UrlTree | null {
    const tree = this.router.parseUrl(url);
    const segments = tree.root.children['primary']?.segments;
    // the page segment: after the brand flavour, or first on a brand host
    const page = brandInPath() ? 1 : 0;
    if (!segments || segments.length < page + 1) return null;

    const twin = DESIGN_TWINS[segments[page].path];
    if (!twin) return null;
    if (twin.exact && segments.length > page + 1) return null;
    if (twin.keepDistributors && distributorLogin) return null;

    segments[page].path = twin.twin;
    if (twin.query) tree.queryParams = { ...twin.query, ...tree.queryParams };
    return tree;
  }

  private runThen(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<GuardResult> {
    const guards = (route.data as DesignRouteData)?.designThen || [];
    return guards.reduce<Observable<GuardResult>>(
      (prev, guardType) =>
        prev.pipe(
          concatMap((result) =>
            result === true
              ? toObservable(
                  this.injector.get(guardType).canActivate(route, state)
                )
              : of(result)
          )
        ),
      of(true)
    );
  }
}

function toObservable(value: any): Observable<GuardResult> {
  if (isObservable(value)) return (value as Observable<GuardResult>).pipe(take(1));
  if (value && typeof value.then === 'function') return from(value as Promise<GuardResult>);
  return of(value as GuardResult).pipe(map((v) => v));
}
